/**
 * Authentication Service
 *
 * Google-only authentication using the Supabase client directly.
 * Email/password methods have been removed — all auth goes through Google OAuth.
 */

import { supabase } from '@/lib/supabase';
import type { User, Session, AuthChangeEvent } from '@supabase/supabase-js';

export const REQUIRED_GMAIL_SCOPES = [
  'https://www.googleapis.com/auth/gmail.settings.basic',
  'https://www.googleapis.com/auth/gmail.labels',
  'https://www.googleapis.com/auth/gmail.readonly',
] as const;

export class AuthService {
  /**
   * Sign in with Google OAuth
   * Redirects the user to Google's consent screen.
   * After authentication, Supabase redirects back and the onAuthStateChange
   * listener picks up the session and triggers provisioning.
   */
  async signInWithGoogle(
    redirectTo?: string,
    selectAccount = false,
  ): Promise<void> {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectTo ?? `${window.location.origin}/today`,
        ...(selectAccount ? { queryParams: { prompt: 'select_account' } } : {}),
      },
    });

    if (error) {
      throw new Error(`Failed to sign in with Google: ${error.message}`);
    }
  }

  /**
   * Request Gmail forwarding scopes via incremental OAuth.
   * Redirects to Google consent screen requesting gmail.settings.* scopes.
   * After consent, Google redirects to `redirectTo` with updated session.
   */
  async requestGmailForwardingAccess(
    redirectTo: string,
    loginHint?: string,
  ): Promise<void> {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        scopes: REQUIRED_GMAIL_SCOPES.join(' '),
        redirectTo,
        queryParams: {
          access_type: 'offline',
          // Never `select_account`: the mailbox we connect must be the account
          // that is signed in (YAR-325). `login_hint` makes Google preselect
          // it; `gmail-forwarding-setup` rejects anything else server-side,
          // because a hint is only a hint — the user can still switch.
          prompt: 'consent',
          ...(loginHint ? { login_hint: loginHint } : {}),
        },
      },
    });
    if (error) {
      throw new Error(`Failed to request Gmail access: ${error.message}`);
    }
  }

  /**
   * Sign out current user
   */
  async signOut(): Promise<void> {
    const { error } = await supabase.auth.signOut();

    if (error) {
      throw new Error(`Failed to sign out: ${error.message}`);
    }
  }

  /**
   * Get current authenticated user
   * Returns null if no session exists (this is normal, not an error)
   */
  async getCurrentUser(): Promise<User | null> {
    // First check if there's a session
    const {
      data: { session },
    } = await supabase.auth.getSession();

    // If no session, return null (this is expected when user is not logged in)
    if (!session) {
      return null;
    }

    // If session exists, get the user
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();

    // Only throw error if there's a session but we can't get the user
    if (error) {
      throw new Error(`Failed to get user: ${error.message}`);
    }

    return user;
  }

  /**
   * Get current session
   * Returns null if no session exists (this is normal, not an error)
   */
  async getSession(): Promise<Session | null> {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();

    // Only throw error for actual failures, not missing sessions
    // Missing session is a normal state (user not logged in)
    if (error && !error.message?.includes('session')) {
      throw new Error(`Failed to get session: ${error.message}`);
    }

    return session;
  }

  /**
   * Subscribe to auth state changes
   * Returns a subscription object with an unsubscribe method
   */
  onAuthStateChange(
    callback: (event: string, session: Session | null) => void,
  ) {
    const { data } = supabase.auth.onAuthStateChange(
      (event: AuthChangeEvent, session: Session | null) => {
        callback(event, session);
      },
    );

    // Return an object with unsubscribe method
    return {
      unsubscribe: () => {
        if (data?.subscription) {
          data.subscription.unsubscribe();
        }
      },
    };
  }
}

export const authService = new AuthService();
